import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import './Auth.css';

// Clé publique Stripe (à configurer dans .env)
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_votre_cle_publique');

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Inter", sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4'
      }
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a'
    }
  },
  hidePostalCode: true
};

const RegisterForm = () => {
  const stripe = useStripe();
  const elements = useElements();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'France'
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!stripe || !elements) {
      setError('Stripe n\'est pas encore chargé. Veuillez réessayer.');
      setLoading(false);
      return;
    }

    try {
      // 1. Créer l'intention de paiement sur le backend
      const { data } = await axios.post('http://localhost:5000/api/payments/create-payment-intent', {
        amount: 19700, // 197€ en centimes
        customerInfo: formData
      });

      // 2. Confirmer le paiement avec Stripe
      const cardElement = elements.getElement(CardElement);
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        data.clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: `${formData.firstName} ${formData.lastName}`,
              email: formData.email,
              phone: formData.phone,
              address: {
                line1: formData.address,
                city: formData.city,
                postal_code: formData.postalCode,
                country: 'FR'
              }
            }
          }
        }
      );

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      // 3. Si paiement réussi, enregistrer l'inscription
      if (paymentIntent.status === 'succeeded') {
        await axios.post('http://localhost:5000/api/registrations', {
          ...formData,
          paymentIntentId: paymentIntent.id,
          amount: 197
        });

        setSuccess(true);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          address: '',
          city: '',
          postalCode: '',
          country: 'France'
        });
        elements.getElement(CardElement).clear();
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.response?.data?.error || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card success-card">
          <div className="success-icon">✓</div>
          <h2>Bienvenue dans F12IA™ !</h2>
          <p className="success-message">
            Félicitations ! Vous avez rejoint <strong>F12IA™</strong> 🎉
          </p>
          <p className="success-details">
            Vous allez recevoir un email de confirmation à l'adresse <strong>{formData.email}</strong> avec :
          </p>
          <ul className="success-list">
            <li>Vos identifiants de connexion à la plateforme</li>
            <li>Le lien d'accès à votre espace formation</li>
            <li>Les bonus exclusifs (prompts IA + guide outils)</li>
            <li>Votre facture</li>
          </ul>
          <p className="success-footer">
            À très bientôt ! 🎉
          </p>
          <a href="/" className="btn btn-primary">Retour à l'accueil</a>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card register-card">
        <h2>Rejoignez F12IA™ – Accès immédiat et à vie</h2>
        <p className="auth-subtitle">
          Une seule app, un seul site ou une seule automatisation peut rembourser la formation. <br />
          Investissement 100 % rentable.
        </p>

        <div className="pricing-recap">
          <div className="pricing-header">
            <h3>Offre de lancement</h3>
            <p className="pricing-subtitle">Limitée aux 100 premiers inscrits</p>
          </div>

          <div className="pricing-total">
            <span>Montant total</span>
            <div className="price-display-register">
              <span className="price-old-register">297€</span>
              <span className="price-current">197€</span>
            </div>
          </div>

          <p className="pricing-note">Paiement unique • Accès immédiat • Accès à vie</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <h3 className="form-section-title">Vos Coordonnées</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">Prénom *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                placeholder="Jean"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Nom *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                placeholder="Dupont"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="jean.dupont@example.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Téléphone *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              placeholder="+33 6 12 34 56 78"
            />
          </div>

          <div className="form-group">
            <label htmlFor="company">Entreprise (optionnel)</label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Ma Société"
            />
          </div>

          <h3 className="form-section-title">Adresse de Facturation</h3>

          <div className="form-group">
            <label htmlFor="address">Adresse *</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              placeholder="123 rue de la République"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="postalCode">Code Postal *</label>
              <input
                type="text"
                id="postalCode"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                required
                placeholder="75001"
              />
            </div>

            <div className="form-group">
              <label htmlFor="city">Ville *</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                placeholder="Paris"
              />
            </div>
          </div>

          <h3 className="form-section-title">Paiement Sécurisé</h3>

          <div className="form-group card-element-group">
            <label>Carte bancaire *</label>
            <div className="card-element-wrapper">
              <CardElement options={CARD_ELEMENT_OPTIONS} />
            </div>
            <p className="card-info">
              🔒 Paiement 100% sécurisé par Stripe
            </p>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-submit"
            disabled={!stripe || loading}
          >
            {loading ? 'Traitement en cours...' : `Rejoindre F12IA™ → 197€`}
          </button>

          <div className="reassurance-section">
            <h3 className="reassurance-title">🛡️ Garantie 30 jours satisfait ou remboursé</h3>
            <p className="reassurance-text">
              Si vous n'économisez pas au moins 9 heures de travail, nous vous remboursons 100 %, sans question.
            </p>

            <div className="reassurance-points">
              <div className="reassurance-item">📧 Accès immédiat après inscription</div>
              <div className="reassurance-item">🎓 Accès à vie à la formation</div>
              <div className="reassurance-item">🔒 Paiement 100 % sécurisé</div>
            </div>

            <h3 className="why-it-works-title">Pourquoi ça marche</h3>
            <div className="why-it-works-points">
              <div className="why-item">✔ Accessible aux débutants</div>
              <div className="why-item">✔ Méthode étape par étape</div>
              <div className="why-item">✔ Résultats rapides dès la première semaine</div>
            </div>

            <p className="final-cta-text">
              <strong>Vous prenez aucun risque.</strong><br />
              Cliquez sur le bouton et commencez à faire coder l'IA à votre place immédiatement.
            </p>
          </div>

          <p className="form-footer">
            En cliquant sur "Payer", vous acceptez nos conditions générales de vente.
            Vous recevrez un email de confirmation après votre inscription.
          </p>
        </form>
      </div>
    </div>
  );
};

const Register = () => {
  return (
    <Elements stripe={stripePromise}>
      <RegisterForm />
    </Elements>
  );
};

export default Register;
